import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/redux/store'
import { menuPathInList, normalizeMenuPath } from '@/utils/menuPathUtils'

const hasCode = (grantedCodes: readonly string[], code: string): boolean => grantedCodes.includes(code)

export const useMenuChucNang = () => {
  const { webUrls, webChucNangs, webChucNangAll, loading, error } = useSelector((state: RootState) => state.menu)

  const normalizedWebUrls = useMemo(
    () => webUrls.map((url) => normalizeMenuPath(url)).filter(Boolean),
    [webUrls],
  )

  const hasMenu = useCallback(
    (urlPath: string): boolean => menuPathInList(urlPath, normalizedWebUrls),
    [normalizedWebUrls],
  )

  const hasChucNang = useCallback(
    (urlPath: string, chucNangCode: string): boolean => {
      if (webChucNangs.length === 0 && webChucNangAll.length > 0) {
        return hasCode(webChucNangAll, chucNangCode)
      }

      const tabData = webChucNangs.find((item: { url: string; chucNangs: string[] }) =>
        menuPathInList(urlPath, [item.url]),
      )

      if (!tabData) {
        return hasCode(webChucNangAll, chucNangCode)
      }

      return hasCode(tabData.chucNangs, chucNangCode)
    },
    [webChucNangs, webChucNangAll],
  )

  const getChucNangsByTab = useCallback(
    (urlPath: string): string[] => {
      const tabData = webChucNangs.find((item: { url: string; chucNangs: string[] }) =>
        menuPathInList(urlPath, [item.url]),
      )
      return tabData ? tabData.chucNangs : []
    },
    [webChucNangs],
  )

  const hasChucNangByCode = useCallback(
    (chucNangCode: string): boolean => hasCode(webChucNangAll, chucNangCode),
    [webChucNangAll],
  )

  const hasAnyChucNangByCode = useCallback(
    (codes: string[]) => codes.some((code) => hasCode(webChucNangAll, code)),
    [webChucNangAll],
  )

  const hasAllChucNangByCode = useCallback(
    (codes: string[]) => codes.every((code) => hasCode(webChucNangAll, code)),
    [webChucNangAll],
  )

  return {
    webUrls,
    webChucNangs,
    webChucNangAll,
    loading,
    error,
    hasMenu,
    hasChucNang,
    getChucNangsByTab,
    hasChucNangByCode,
    hasAnyChucNangByCode,
    hasAllChucNangByCode,
  }
}
